import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertDialog, Button, Callout, Card, Checkbox, DateField, EmptyState, Heading, Modal,
  SelectField, Spinner, Tag, TextField, Toast,
} from '@humanforce/mango-ui-react';
import type { TableColumn, ToastRef } from '@humanforce/mango-ui-react';
import {
  PUBLIC_HOLIDAY_REGIONS, PublicHolidayCalendarMonth, PublicHolidayDayDetail, PublicHolidayList,
  type PublicHolidayDto, type PublicHolidayListRow, type PublicHolidayRegionCode,
} from '@pay-roadrunner/public-holidays-ui';
import '@pay-roadrunner/public-holidays-ui/publicHolidayCalendar.css';
import {
  createTenantPublicHoliday, deleteTenantPublicHoliday, getImportablePublicHolidayCalendarYears,
  getTenantPublicHolidays, importPublicHolidays, previewPublicHolidays, PublicHolidaysApiError,
  updateTenantPublicHoliday, type ImportablePublicHolidayCalendarYear, type TenantPublicHoliday,
} from '../publicHolidaysApi';

interface Props { businessRecordId: string; businessRecordName: string; regionCode: string }
interface HolidayDraft { name: string; jurisdictionCode: string; holidayDate: string; observedDate: string; isPartialDay: boolean }

const emptyDraft: HolidayDraft = { name: '', jurisdictionCode: '', holidayDate: '', observedDate: '', isPartialDay: false };
const formatDate = (value: string | null) => value
  ? new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';

const tenantColumns: TableColumn[] = [
  { field: 'holidayDate', header: 'Date', sortable: true, body: (row: PublicHolidayListRow) => formatDate(row.holidayDate) },
  { field: 'name', header: 'Holiday', sortable: true },
  { field: 'scope', header: 'Applies to', sortable: true, body: (row: PublicHolidayListRow) => row.jurisdictionCode || 'National' },
  { field: 'observedDate', header: 'Observed date', body: (row: PublicHolidayListRow) => formatDate(row.observedDate) },
  { field: 'isPartialDay', header: 'Duration', body: (row: PublicHolidayListRow) => row.isPartialDay ? 'Partial day' : 'Full day' },
  {
    field: 'isSystemOwned', header: 'Type', body: (row: PublicHolidayListRow) => (
      <Tag label={row.isSystemOwned ? 'Imported' : 'Custom'} tone={row.isSystemOwned ? 'blue' : 'grey'} />
    ),
  },
];

export const PublicHolidaysPanel = ({ businessRecordId, businessRecordName, regionCode }: Props) => {
  const normalizedRegionCode = regionCode.toUpperCase() === 'GB' ? 'UK' : regionCode.toUpperCase();
  const holidayRegionCode = normalizedRegionCode in PUBLIC_HOLIDAY_REGIONS
    ? normalizedRegionCode as PublicHolidayRegionCode
    : undefined;
  const toast = useRef<ToastRef>(null);
  const [years, setYears] = useState<ImportablePublicHolidayCalendarYear[]>([]);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState<TenantPublicHoliday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmingImport, setConfirmingImport] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRows, setPreviewRows] = useState<PublicHolidayDto[]>([]);
  const [previewError, setPreviewError] = useState('');
  const [previewMonth, setPreviewMonth] = useState(new Date());
  const [selectedHoliday, setSelectedHoliday] = useState<PublicHolidayDto>();
  const [editing, setEditing] = useState<TenantPublicHoliday>();
  const [editorVisible, setEditorVisible] = useState(false);
  const [draft, setDraft] = useState<HolidayDraft>(emptyDraft);
  const [submitted, setSubmitted] = useState(false);
  const [deleting, setDeleting] = useState<TenantPublicHoliday>();

  const load = useCallback(async () => {
    setLoading(true); setError(''); setPermissionDenied(false);
    try {
      const [availableYears, holidays] = await Promise.all([
        getImportablePublicHolidayCalendarYears(businessRecordId), getTenantPublicHolidays(businessRecordId),
      ]);
      setYears(availableYears); setRows(holidays);
      const available = new Set([...availableYears.map((year) => year.calendarYear), ...holidays.map((holiday) => holiday.calendarYear)]);
      setCalendarYear((current) => available.has(current) ? current : [...available].sort()[0] ?? new Date().getFullYear());
    } catch (reason) {
      const denied = reason instanceof PublicHolidaysApiError && (reason.status === 401 || reason.status === 403);
      setPermissionDenied(denied);
      setError(denied ? '' : reason instanceof Error ? reason.message : 'Public holidays could not be loaded.');
    } finally { setLoading(false); }
  }, [businessRecordId]);

  useEffect(() => { void load(); }, [load]);

  const visibleRows = useMemo(() => rows.filter((row) => row.calendarYear === calendarYear), [calendarYear, rows]);
  const yearOptions = useMemo(() => [...new Set([...years.map((year) => year.calendarYear), ...rows.map((row) => row.calendarYear)])]
    .sort().map((year) => ({ value: String(year), label: String(year) })), [rows, years]);
  const selectedImportYear = years.find((year) => year.calendarYear === calendarYear);
  const jurisdictionOptions = holidayRegionCode ? PUBLIC_HOLIDAY_REGIONS[holidayRegionCode].jurisdictions : [];
  const holidayDateOutsideSelectedYear = !editing && Boolean(draft.holidayDate) && Number(draft.holidayDate.slice(0, 4)) !== calendarYear;

  const notifyFailure = (summary: string, reason: unknown) => {
    toast.current?.clear();
    toast.current?.show({ tone: 'critical', summary, detail: reason instanceof Error ? reason.message : 'Try again.', sticky: true });
  };

  const runImport = async (confirmReplaceExisting: boolean) => {
    setSaving(true);
    try {
      const result = await importPublicHolidays(businessRecordId, calendarYear, confirmReplaceExisting);
      setConfirmingImport(false); setPreviewOpen(false); await load();
      toast.current?.show({ tone: 'positive', summary: 'Public holidays imported', detail: `${result.importedCount} imported; ${result.replacedCount} previous records replaced.` });
    } catch (reason) { notifyFailure('Public holidays could not be imported', reason); }
    finally { setSaving(false); }
  };

  const openPreview = async () => {
    if (!selectedImportYear) return;
    setPreviewOpen(true); setPreviewLoading(true); setPreviewError(''); setSelectedHoliday(undefined);
    try {
      const holidays = await previewPublicHolidays(businessRecordId, calendarYear);
      setPreviewRows(holidays);
      const first = [...holidays].sort((a, b) => a.holidayDate.localeCompare(b.holidayDate))[0]?.holidayDate ?? selectedImportYear.startDate;
      setPreviewMonth(new Date(`${first.slice(0, 10)}T00:00:00`));
    } catch (reason) { setPreviewError(reason instanceof Error ? reason.message : 'The preview could not be loaded.'); }
    finally { setPreviewLoading(false); }
  };

  const openCreate = () => { setEditing(undefined); setDraft(emptyDraft); setSubmitted(false); setEditorVisible(true); };
  const openEdit = (holiday: TenantPublicHoliday) => {
    setEditing(holiday); setSubmitted(false); setEditorVisible(true);
    setDraft({ name: holiday.name, jurisdictionCode: holiday.jurisdictionCode ?? '', holidayDate: holiday.holidayDate, observedDate: holiday.observedDate, isPartialDay: holiday.isPartialDay });
  };
  const closeEditor = () => { setEditorVisible(false); setEditing(undefined); setDraft(emptyDraft); setSubmitted(false); };

  const saveHoliday = async () => {
    setSubmitted(true);
    if (!draft.name.trim() || (!editing && (!draft.holidayDate || holidayDateOutsideSelectedYear)) || (editing && !draft.observedDate)) return;
    setSaving(true);
    try {
      if (editing) await updateTenantPublicHoliday(editing.id, {
        businessRecordId, observedDate: draft.observedDate, expectedVersion: editing.version,
        ...(editing.isSystemOwned ? {} : { name: draft.name.trim(), jurisdictionCode: draft.jurisdictionCode || null, isPartialDay: draft.isPartialDay }),
      });
      else await createTenantPublicHoliday({ businessRecordId, name: draft.name.trim(), jurisdictionCode: draft.jurisdictionCode || null, holidayDate: draft.holidayDate, isPartialDay: draft.isPartialDay });
      const summary = editing ? 'Public holiday updated' : 'Public holiday added';
      closeEditor(); await load(); toast.current?.show({ tone: 'positive', summary });
    } catch (reason) { notifyFailure('Public holiday could not be saved', reason); }
    finally { setSaving(false); }
  };

  const removeHoliday = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await deleteTenantPublicHoliday(deleting.id, businessRecordId, deleting.version);
      setDeleting(undefined); await load(); toast.current?.show({ tone: 'positive', summary: 'Public holiday deleted' });
    } catch (reason) { notifyFailure('Public holiday could not be deleted', reason); }
    finally { setSaving(false); }
  };

  const renderActions = (row: PublicHolidayListRow) => {
    const holiday = visibleRows.find((candidate) => candidate.id === row.id);
    if (!holiday) return null;
    return <div className="mgo-cluster mgo-gap-xs">
      <Button label="Edit" variant="tertiary" size="small" onClick={() => openEdit(holiday)} />
      {!holiday.isSystemOwned ? <Button label="Delete" variant="tertiary" size="small" onClick={() => setDeleting(holiday)} /> : null}
    </div>;
  };

  if (loading) return <div className="mgo-stack mgo-gap-l"><Heading level={2}>Import public holidays</Heading><Card><div className="mgo-stack mgo-justify-center mgo-align-center mgo-padding-l" role="status" aria-label="Loading public holidays"><Spinner /></div></Card></div>;
  if (permissionDenied) return <div className="mgo-stack mgo-gap-l"><Heading level={2}>Import public holidays</Heading><Card><EmptyState variant="warning" title="Public holidays unavailable" description="You do not have permission to manage public holidays for this Business Record." /></Card></div>;
  if (error) return <div className="mgo-stack mgo-gap-l"><Heading level={2}>Import public holidays</Heading><Callout tone="critical" title="Unable to load public holidays" action={<Button label="Try again" variant="secondary" onClick={() => void load()} />}>Your data has not been changed.</Callout></div>;

  return <div className="mgo-stack mgo-gap-xl">
    <div className="mgo-stack mgo-gap-xs">
      <Heading level={2}>Import public holidays</Heading>
      <p className="mgo-text-s mgo-color-muted">Manage the national, state and custom holidays used for this Business Record.</p>
    </div>

    <div style={{ maxWidth: '12.5rem' }}>
      <SelectField label="Calendar year" value={String(calendarYear)} options={yearOptions} searchable={false} onChange={(value) => setCalendarYear(Number(value))} />
    </div>

    <Card header={<div className="mgo-split mgo-gap-m">
      <div className="mgo-stack mgo-gap-0">
        <span>{calendarYear} holidays</span>
        <span className="mgo-text-xs mgo-color-muted">{visibleRows.length} {visibleRows.length === 1 ? 'holiday' : 'holidays'}</span>
      </div>
      <div className="mgo-cluster mgo-gap-s mgo-justify-end">
        <Button label="Import" variant="secondary" size="small" disabled={!selectedImportYear} tooltip={!selectedImportYear ? `No published catalogue is available for ${calendarYear}.` : undefined} onClick={() => void openPreview()} />
        <Button label="Add" size="small" onClick={openCreate} />
      </div>
    </div>}>
      {visibleRows.length ? <PublicHolidayList mode="tenant" rows={visibleRows} columns={tenantColumns} renderActions={renderActions} />
        : <div className="mgo-stack mgo-justify-center mgo-align-center mgo-padding-l" style={{ minHeight: '12rem' }}><EmptyState variant="no-data" title={`No holidays for ${calendarYear}`} description="Add a custom holiday or import the published national and state-based holidays for this calendar year." /></div>}
    </Card>

    <Modal visible={editorVisible} header={editing ? `Edit ${editing.name}` : 'Add public holiday'} onHide={closeEditor} footer={<><Button label="Cancel" variant="secondary" onClick={closeEditor} /><Button label={editing ? 'Save changes' : 'Add holiday'} loading={saving} onClick={() => void saveHoliday()} /></>}>
      <div className="mgo-stack mgo-gap-l">
        <TextField label="Holiday name" required value={draft.name} disabled={editing?.isSystemOwned} onChange={(event) => setDraft({ ...draft, name: event.target.value })} status={submitted && !draft.name.trim() ? 'critical' : undefined} statusText={submitted && !draft.name.trim() ? 'Enter a holiday name.' : undefined} />
        {!editing ? <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-m">
          <DateField label="Holiday date" dateFormat="ymd" fullWidth required value={draft.holidayDate} onChange={(holidayDate) => setDraft({ ...draft, holidayDate })} status={submitted && (!draft.holidayDate || holidayDateOutsideSelectedYear) ? 'critical' : undefined} statusText={submitted && !draft.holidayDate ? 'Select a holiday date.' : submitted && holidayDateOutsideSelectedYear ? `Select a date in ${calendarYear}.` : undefined} />
          {holidayRegionCode && jurisdictionOptions.length > 0 ? <SelectField label={PUBLIC_HOLIDAY_REGIONS[holidayRegionCode].jurisdictionLabel} value={draft.jurisdictionCode} searchable={false} options={[{ value: '', label: 'National' }, ...jurisdictionOptions]} onChange={(jurisdictionCode) => setDraft({ ...draft, jurisdictionCode: String(jurisdictionCode) })} /> : null}
        </div> : <DateField label="Observed date" dateFormat="ymd" fullWidth required value={draft.observedDate} onChange={(observedDate) => setDraft({ ...draft, observedDate })} status={submitted && !draft.observedDate ? 'critical' : undefined} statusText={submitted && !draft.observedDate ? 'Select an observed date.' : undefined} />}
        {!editing?.isSystemOwned ? <Checkbox id="tenant-public-holiday-partial-day" label="Partial day" helpText="Select this when the holiday applies for only part of the day." checked={draft.isPartialDay} onChange={(isPartialDay) => setDraft({ ...draft, isPartialDay })} />
          : <Callout tone="info" title="Imported holiday">Only the observed date can be changed. Importing this calendar year again restores published holiday details from the catalogue.</Callout>}
      </div>
    </Modal>

    <Modal visible={previewOpen} header={`Import public holidays — ${calendarYear}`} size="large" onHide={() => setPreviewOpen(false)} footer={<><Button label="Cancel" variant="secondary" onClick={() => setPreviewOpen(false)} /><Button label="Import holidays" loading={saving} disabled={previewLoading || Boolean(previewError)} onClick={() => visibleRows.some((row) => row.isSystemOwned) ? setConfirmingImport(true) : void runImport(false)} /></>}>
      <div className="mgo-stack mgo-gap-l">
        {previewLoading ? <div role="status" aria-label="Loading public holiday preview" className="mgo-cluster mgo-justify-center mgo-padding-l"><Spinner /></div> : null}
        {previewError ? <Callout tone="critical" title="Preview unavailable">{previewError}</Callout> : null}
        {!previewLoading && !previewError ? <>
          <Callout tone="info" title={`${previewRows.length} holidays ready to import`}>This imports the published national and state-based holidays for {calendarYear}. Custom holidays are preserved.</Callout>
          <PublicHolidayCalendarMonth compact locale="en-AU" month={previewMonth} rows={previewRows} onMonthChange={setPreviewMonth} onEdit={setSelectedHoliday} />
          {selectedHoliday ? <PublicHolidayDayDetail holiday={selectedHoliday} /> : null}
        </> : null}
      </div>
    </Modal>

    <AlertDialog visible={confirmingImport} header="Replace imported public holidays?" severity="warning" confirmLabel="Replace and import" cancelLabel="Cancel" confirmLoading={saving} onConfirm={() => void runImport(true)} onCancel={() => setConfirmingImport(false)}>This replaces imported holidays for {calendarYear} and preserves custom holidays for {businessRecordName}.</AlertDialog>
    <AlertDialog visible={Boolean(deleting)} header="Delete custom public holiday?" severity="warning" confirmLabel="Delete" cancelLabel="Cancel" confirmLoading={saving} onConfirm={() => void removeHoliday()} onCancel={() => setDeleting(undefined)}>Delete {deleting?.name}? This cannot be undone.</AlertDialog>
    <Toast ref={toast} />
  </div>;
};
